import React, { useCallback, useEffect, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import {
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Table,
  TextField,
  Textarea,
  styles as ui,
  useConfirm,
  useToast,
} from "stories/Admin";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import {
  categoryInfo,
  categoryLabel,
  CONTACT_BASE,
  ContactMessage,
  ContactPage,
  contactQuery,
  errorText,
  firstLine,
  formatOslo,
  platformLabel,
  replyMailto,
  requestInquiryRefresh,
  senderText,
} from "./inquiries";
import styles from "./Inquiries.module.scss";

/*
 * Superadmin: «Kontakt oss»-meldinger fra appene og ibønn.no. Liste med filtre og søk,
 * og et detaljpanel med hele meldingen, enhetsinfo, lest/ulest, arkiv, slett, notat og svar på e-post.
 */

const PAGE_SIZE = 50;

const statusOptions = [
  { value: "NEW,READ", label: "Innboks" },
  { value: "NEW", label: "Uleste" },
  { value: "READ", label: "Leste" },
  { value: "ARCHIVED", label: "Arkivert" },
  { value: "", label: "Alle" },
];

const categoryOptions = [
  { value: "", label: "Alle kategorier" },
  ...Object.entries(categoryInfo).map(([value, info]) => ({
    value,
    label: info.label,
  })),
];

const ContactInboxPage: React.FC = () => {
  const { execute, lastError } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const toast = useToast();
  const confirm = useConfirm();

  const [data, setData] = useState<ContactPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [status, setStatus] = useState("NEW,READ");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ContactMessage | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setIsLoading(true);
    const result = (await executeRef.current(
      contactQuery({
        status,
        category,
        q: query,
        page: page - 1,
        size: PAGE_SIZE,
      }),
      "GET",
    )) as ContactPage | null;
    if (result) setData(result);
    setFailed(!result);
    setIsLoading(false);
  }, [status, category, query, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setNote(selected?.adminNote ?? ""), [selected?.id]);

  const replace = (updated: ContactMessage) => {
    setSelected((s) => (s?.id === updated.id ? updated : s));
    setData((d) =>
      d
        ? {
            ...d,
            items: d.items.map((m) => (m.id === updated.id ? updated : m)),
          }
        : d,
    );
  };

  const patch = async (
    m: ContactMessage,
    body: { status?: string; adminNote?: string },
    action: string,
  ) => {
    setBusy(action);
    const updated = (await executeRef.current(
      `${CONTACT_BASE}/${m.id}`,
      "PATCH",
      body,
    )) as ContactMessage | null;
    setBusy(null);
    if (!updated) {
      toast.error(errorText(lastError(), "Kunne ikke lagre endringen."));
      return null;
    }
    replace(updated);
    requestInquiryRefresh();
    return updated;
  };

  const open = async (m: ContactMessage) => {
    setSelected(m);
    // Åpnet = lest. «Merk som ulest» angrer.
    if (m.status === "NEW") await patch(m, { status: "READ" }, "open");
  };

  const remove = async (m: ContactMessage) => {
    const ok = await confirm({
      title: "Slett meldingen?",
      message: (
        <>
          Meldingen fra <b>{senderText(m)}</b> slettes permanent. Dette kan ikke
          angres.
        </>
      ),
      confirmLabel: "Slett",
      tone: "danger",
    });
    if (!ok) return;
    setBusy("delete");
    await executeRef.current(`${CONTACT_BASE}/${m.id}`, "DELETE");
    const error = lastError();
    setBusy(null);
    if (error) {
      toast.error(errorText(error, "Kunne ikke slette meldingen."));
      return;
    }
    toast.success("Meldingen er slettet.");
    setSelected(null);
    requestInquiryRefresh();
    load();
  };

  const columns: Array<Column<ContactMessage>> = [
    {
      key: "category",
      header: "Kategori",
      render: (m) => (
        <Badge tone={categoryInfo[m.category]?.tone ?? "grey"}>
          {categoryLabel(m.category)}
        </Badge>
      ),
    },
    {
      key: "from",
      header: "Fra",
      render: (m) => (
        <span className={styles.stack}>
          <span
            className={`${styles.clamp} ${m.status === "NEW" ? styles.unread : ""}`}
          >
            {m.name || m.email || "Anonym"}
          </span>
          {m.name && m.email && <span className={styles.muted}>{m.email}</span>}
        </span>
      ),
    },
    {
      key: "message",
      header: "Melding",
      render: (m) => (
        <span className={styles.stack}>
          <span
            className={`${styles.clamp} ${m.status === "NEW" ? styles.unread : ""}`}
          >
            {firstLine(m.message)}
          </span>
          {(m.mosqueName || m.mosqueOrgNr) && (
            <span className={styles.muted}>
              {m.mosqueName || m.mosqueOrgNr}
            </span>
          )}
        </span>
      ),
    },
    {
      key: "platform",
      header: "Plattform",
      render: (m) => (
        <span className={styles.stack}>
          <span>{platformLabel(m.platform)}</span>
          {m.appVersion && (
            <span className={styles.muted}>v{m.appVersion}</span>
          )}
        </span>
      ),
    },
    {
      key: "time",
      header: "Tid",
      numeric: true,
      render: (m) => formatOslo(m.createdAt),
    },
  ];

  const pageCount = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <>
      <PageHeader
        title="Henvendelser"
        subtitle="Meldinger fra «Kontakt oss» i appene og på ibønn.no."
        actions={
          <Button
            variant="secondary"
            icon={<Icon name="swap" size={16} />}
            onClick={() => {
              load();
              requestInquiryRefresh();
            }}
            loading={isLoading}
          >
            Oppdater
          </Button>
        }
      />
      <SectionTabs />

      <div className={styles.split}>
        <Card className={styles.tableCard}>
          <div className={styles.filters}>
            <TextField
              label="Søk"
              placeholder="Søk i melding, navn, e-post, moské eller notat"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fieldClassName={styles.searchField}
            />
            <Select
              label="Vis"
              value={status}
              options={statusOptions}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            />
            <Select
              label="Kategori"
              value={category}
              options={categoryOptions}
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {failed && !data ? (
            <EmptyState icon="alert" title="Kunne ikke hente meldingene">
              Prøv igjen med «Oppdater».
            </EmptyState>
          ) : isLoading && !data ? (
            <div className={styles.skeletons}>
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} height={44} />
              ))}
            </div>
          ) : (
            <>
              <Table
                columns={columns}
                rows={data?.items ?? []}
                rowKey={(m) => m.id}
                onRowClick={open}
                rowState={(m) =>
                  m.id === selected?.id
                    ? "on"
                    : m.status === "ARCHIVED"
                      ? "dim"
                      : undefined
                }
                caption="Meldinger"
                empty={
                  <EmptyState icon="inbox" title="Ingen meldinger">
                    Ingen meldinger passer filtrene.
                  </EmptyState>
                }
              />
              <div className={styles.footer}>
                <span className={styles.muted}>
                  {data
                    ? `${data.total.toLocaleString("nb-NO")} meldinger · ${data.unread.toLocaleString("nb-NO")} uleste`
                    : ""}
                </span>
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onChange={setPage}
                />
              </div>
            </>
          )}
        </Card>

        <div className={styles.detail}>
          {selected ? (
            <Card
              title={categoryLabel(selected.category)}
              actions={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelected(null)}
                  aria-label="Lukk"
                >
                  <Icon name="x" size={16} />
                </Button>
              }
            >
              <div className={styles.detailHead}>
                <Badge
                  tone={
                    selected.status === "NEW"
                      ? "gold"
                      : selected.status === "ARCHIVED"
                        ? "grey"
                        : "mint"
                  }
                >
                  {selected.status === "NEW"
                    ? "Ulest"
                    : selected.status === "ARCHIVED"
                      ? "Arkivert"
                      : "Lest"}
                </Badge>
                <span className={styles.muted}>
                  {formatOslo(selected.createdAt)}
                </span>
              </div>

              <p className={styles.body}>{selected.message}</p>

              <div className={styles.actions}>
                {selected.email && (
                  <a
                    className={`${ui.btn} ${ui.sm}`}
                    href={replyMailto(selected.email)}
                  >
                    <Icon name="link" size={16} />
                    Svar på e-post
                  </a>
                )}
                {selected.status === "NEW" ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={busy === "read"}
                    onClick={() => patch(selected, { status: "READ" }, "read")}
                  >
                    Merk som lest
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={busy === "unread"}
                    onClick={() => patch(selected, { status: "NEW" }, "unread")}
                  >
                    Merk som ulest
                  </Button>
                )}
                {selected.status === "ARCHIVED" ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={busy === "archive"}
                    onClick={() =>
                      patch(selected, { status: "READ" }, "archive")
                    }
                  >
                    Flytt til innboks
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={busy === "archive"}
                    onClick={() =>
                      patch(selected, { status: "ARCHIVED" }, "archive")
                    }
                  >
                    Arkiver
                  </Button>
                )}
                <Button
                  variant="danger"
                  size="sm"
                  icon={<Icon name="trash" size={16} />}
                  loading={busy === "delete"}
                  onClick={() => remove(selected)}
                >
                  Slett
                </Button>
              </div>

              <dl className={styles.details}>
                <dt>Fra</dt>
                <dd>{senderText(selected)}</dd>
                <dt>Moské</dt>
                <dd>
                  {selected.mosqueName
                    ? `${selected.mosqueName} (${selected.mosqueOrgNr})`
                    : selected.mosqueOrgNr || "–"}
                </dd>
                <dt>Plattform</dt>
                <dd>
                  {platformLabel(selected.platform)}
                  {selected.appVersion && ` · iBønn ${selected.appVersion}`}
                </dd>
                <dt>Enhet</dt>
                <dd>
                  {[selected.deviceModel, selected.osVersion]
                    .filter(Boolean)
                    .join(" · ") || "–"}
                </dd>
                <dt>Firebase-UID</dt>
                <dd>
                  {selected.firebaseUid ? (
                    <code>{selected.firebaseUid}</code>
                  ) : (
                    "–"
                  )}
                </dd>
              </dl>

              <Textarea
                label="Notat (bare for superadmin)"
                value={note}
                maxChars={4000}
                rows={3}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className={styles.noteActions}>
                <Button
                  size="sm"
                  disabled={note.trim() === (selected.adminNote ?? "").trim()}
                  loading={busy === "note"}
                  onClick={async () => {
                    if (await patch(selected, { adminNote: note }, "note")) {
                      toast.success("Notatet er lagret.");
                    }
                  }}
                >
                  Lagre notat
                </Button>
              </div>
            </Card>
          ) : (
            <Card>
              <EmptyState icon="inbox" title="Velg en melding">
                Klikk på en melding for å lese den. Den merkes da som lest.
              </EmptyState>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default ContactInboxPage;
